import cuid from "cuid";
import { Formik } from "formik";
import { defaultsDeep, find, get, isEmpty } from "lodash";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { BiUserCircle } from "react-icons/bi";
import { BsPhone } from "react-icons/bs";
import Card from "shared/components/Card";
import Header from "shared/components/Header";
import Input from "shared/components/Input";
import {
  ActionStepper,
  ProductCard,
} from "shared/components/SmartphoneWidgets";
import { NAME_SPACES } from "shared/locales/constants";
import { CONTACT_POINT_SYSTEM } from "utils/constants";
import { bindInputProps } from "utils/helpers/input";
import validation from "./validation";

const Contact = ({ onNext, onBack, project }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
  const DETAIL = t("DETAIL", {
    returnObjects: true,
  });
  const TERMINAL_CONTACT = t("DETAIL.TEMPLATES.TERMINAL_CONTACT", {
    returnObjects: true,
  });

  const onSubmit = ({ contact, phoneNumber }) => {
    onNext({
      payload: {
        visible: true,
        contract: { id: cuid(), issuanceDate: moment() },
        contact: {
          ...contact,
          telecoms: [
            ...contact.telecoms.filter(
              ({ system }) => system !== CONTACT_POINT_SYSTEM.PHONE,
            ),
            ...(phoneNumber.value
              ? [
                  {
                    ...phoneNumber,
                    value: "+689" + phoneNumber.value.toString(),
                  },
                ]
              : []),
          ],
        },
      },
    });
  };

  const phoneNumber = find(get(project, "contact.telecoms", []), {
    system: CONTACT_POINT_SYSTEM.PHONE,
  });

  return (
    <>
      <Header key={"header"} title={DETAIL.NEW_QUOTE} />
      <Formik
        onSubmit={onSubmit}
        initialValues={{
          contact: defaultsDeep(get(project, "contact", {}), {
            id: cuid(),
            user: { id: cuid(), firstname: null, lastname: null },
            telecoms: [],
          }),
          phoneNumber: phoneNumber
            ? { ...phoneNumber, value: phoneNumber.value.replace("+689", "") }
            : { id: cuid(), value: null, system: CONTACT_POINT_SYSTEM.PHONE },
        }}
        validationSchema={validation(
          t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
        )}
      >
        {({ handleSubmit, ...formProps }) => [
          <Card
            key="card"
            type={"custom"}
            styleType={"bordered"}
            size={"medium"}
            head
            title={TERMINAL_CONTACT.TITLE}
            subtitle={TERMINAL_CONTACT.SUBTITLE}
          >
            <div className="m-top-24" />
            <h1 className="title">
              <span>{TERMINAL_CONTACT.TERMINAL_INFO}</span>
            </h1>
            <div className="m-top-8" />
            <Card type={"custom"} styleType={"shadow"}>
              <div className="grid--1">
                <ProductCard
                  image={get(project, "fields.terminal.image", "")}
                  name={get(project, "fields.terminal.model", "")}
                  price={`${get(
                    project,
                    "fields.terminal.purchasePrice",
                    "",
                  )} XPF`}
                  onModify={onBack}
                />
              </div>
            </Card>
            <div className="m-top-24" />
            <h1 className="title">
              <span>{TERMINAL_CONTACT.ADHERENT}</span>
            </h1>
            <p className="subtitle">{TERMINAL_CONTACT.ADHERENT_COORDINATES}</p>
            <div className="m-top-8" />
            <Card type={"custom"} styleType={"shadow"}>
              <div className="grid--1">
                <Input
                  label={TERMINAL_CONTACT.LAST_NAME}
                  placeholder={TERMINAL_CONTACT.LAST_NAME}
                  icon={<BiUserCircle size={16} />}
                  required
                  {...bindInputProps({
                    name: "contact.user.lastname",
                    ...formProps,
                  })}
                />
                <Input
                  label={TERMINAL_CONTACT.FIRST_NAME}
                  placeholder={TERMINAL_CONTACT.FIRST_NAME}
                  icon={<BiUserCircle size={16} />}
                  required
                  {...bindInputProps({
                    name: "contact.user.firstname",
                    ...formProps,
                  })}
                />
                <Input
                  label={TERMINAL_CONTACT.VINI_NUMBER}
                  placeholder={TERMINAL_CONTACT.VINI_NUMBER}
                  icon={<BsPhone size={16} />}
                  required
                  {...bindInputProps({
                    name: "phoneNumber.value",
                    ...formProps,
                  })}
                />
              </div>
            </Card>
          </Card>,
          <Card type={"custom"} styleType={"transparent"} key="actions">
            <ActionStepper
              onPrevious={onBack}
              onNext={handleSubmit}
              isNextDisabled={
                !isEmpty(formProps.errors) ||
                !get(formProps, "values.contact.user.firstname")
              }
            />
          </Card>,
        ]}
      </Formik>
    </>
  );
};

export default Contact;
